/**
 * Financial Reconciliation — Phase 8 ledger, summaries, zero-balance validation, print/PDF export.
 * Persists to settlement frame_data: frame id `sframe-finance-reconciliation`.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSettlementSession } from '../../hooks/useSettlementSession';

export type LedgerDirection = 'in' | 'out';

export interface LedgerTransaction {
  id: string;
  date: string;
  description: string;
  category: string;
  direction: LedgerDirection;
  amountCents: number;
  createdAt: string;
}

const FRAME_ID = 'sframe-finance-reconciliation';

function formatMoney(cents: number): string {
  const sign = cents < 0 ? '-' : '';
  const abs = Math.abs(cents);
  return `${sign}$${(abs / 100).toFixed(2)}`;
}

function newId() {
  return typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `tx-${Date.now()}`;
}

export interface FinancialReconciliationProps {
  estateId: string | null;
  readOnly?: boolean;

}

export default function FinancialReconciliation({ estateId, readOnly }: FinancialReconciliationProps) {
  const { loadFrameData, saveFrameData, isLoading, error } = useSettlementSession(estateId, { readOnly });
  const [transactions, setTransactions] = useState<LedgerTransaction[]>([]);
  const [confirmedZero, setConfirmedZero] = useState(false);
  const [dirty, setDirty] = useState(false);

  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    description: '',
    category: 'General',
    direction: 'in' as LedgerDirection,
    amount: '',
  });

  useEffect(() => {
    if (!estateId) return;
    (async () => {
      const row = await loadFrameData(FRAME_ID);
      const data = (row?.data || {}) as Record<string, unknown>;
      const txs = Array.isArray(data.transactions) ? (data.transactions as LedgerTransaction[]) : [];
      setTransactions(txs);
      setConfirmedZero(!!data.confirmedZero);
      setDirty(false);
    })();
  }, [estateId, loadFrameData]);

  const { totalIn, totalOut, balanceCents } = useMemo(() => {
    let income = 0;
    let expense = 0;
    for (const t of transactions) {
      const c = Number(t.amountCents) || 0;
      if (t.direction === 'in') income += c;
      else expense += c;
    }
    return { totalIn: income, totalOut: expense, balanceCents: income - expense };
  }, [transactions]);

  const byCategory = useMemo(() => {
    const map = new Map<string, { in: number; out: number }>();
    for (const t of transactions) {
      const key = t.category || 'Uncategorized';
      const cur = map.get(key) || { in: 0, out: 0 };
      const c = Number(t.amountCents) || 0;
      if (t.direction === 'in') cur.in += c;
      else cur.out += c;
      map.set(key, cur);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [transactions]);

  const persist = useCallback(async () => {
    if (!estateId || readOnly) return;
    const ok = await saveFrameData(FRAME_ID, {
      transactions,
      confirmedZero,
      updatedAt: new Date().toISOString(),
    });
    if (ok) setDirty(false);
  }, [estateId, readOnly, saveFrameData, transactions, confirmedZero]);

  useEffect(() => {
    if (!dirty || readOnly || !estateId) return;
    const t = setTimeout(() => {
      void persist();
    }, 800);
    return () => clearTimeout(t);
  }, [dirty, persist, readOnly, estateId, transactions, confirmedZero]);

  const addTransaction = () => {
    if (readOnly) return;
    const dollars = parseFloat(form.amount);
    if (!form.description.trim() || Number.isNaN(dollars) || dollars <= 0) return;
    const amountCents = Math.round(dollars * 100);
    const tx: LedgerTransaction = {
      id: newId(),
      date: form.date,
      description: form.description.trim(),
      category: form.category.trim() || 'General',
      direction: form.direction,
      amountCents,
      createdAt: new Date().toISOString(),
    };
    setTransactions((prev) => [tx, ...prev]);
    setForm((f) => ({ ...f, description: '', amount: '' }));
    setConfirmedZero(false);
    setDirty(true);
  };

  const removeTransaction = (id: string) => {
    if (readOnly) return;
    setTransactions((prev) => prev.filter((t) => t.id !== id));
    setConfirmedZero(false);
    setDirty(true);
  };

  const handlePrintPdf = () => {
    window.print();
  };

  const zeroOk = balanceCents === 0;

  return (
    <div className="space-y-8">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-lg p-3">{error}</div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-xl border border-slate-200 bg-emerald-50/80 p-4">
          <p className="text-xs font-medium text-emerald-800 uppercase tracking-wide">Total income</p>
          <p className="text-2xl font-semibold text-emerald-950 mt-1">{formatMoney(totalIn)}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-rose-50/80 p-4">
          <p className="text-xs font-medium text-rose-800 uppercase tracking-wide">Total expenses</p>
          <p className="text-2xl font-semibold text-rose-950 mt-1">{formatMoney(totalOut)}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <p className="text-xs font-medium text-slate-600 uppercase tracking-wide">Net balance</p>
          <p className={`text-2xl font-semibold mt-1 ${zeroOk ? 'text-emerald-700' : 'text-slate-900'}`}>
            {formatMoney(balanceCents)}
          </p>
          <p className="text-xs text-slate-500 mt-2">
            Final estate cash position must reconcile to <span className="font-semibold">$0.00</span> before closure.
          </p>
        </div>
      </div>
      <div className="rounded-xl border border-slate-200 overflow-hidden">
        <div className="bg-slate-50 border-b border-slate-200 px-5 py-4 flex items-center gap-3">
          <span className="text-lg font-medium text-cyan-900">Ledger</span>
          <span className="inline-flex items-center rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-500">
            Optional
          </span>
        </div>
        <div className="p-5 space-y-6">
        {!readOnly && (
          <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
            <h3 className="text-sm font-semibold text-slate-800">Add ledger entry</h3>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
              <label className="text-xs text-slate-600 md:col-span-1">
                Date
                <input
                  type="date"
                  className="mt-1 w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm"
                  value={form.date}
                  onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                />
              </label>
              <label className="text-xs text-slate-600 md:col-span-2">
                Description
                <input
                  className="mt-1 w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm"
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="e.g. Final distribution wire"
                />
              </label>
              <label className="text-xs text-slate-600 md:col-span-1">
                Category
                <input
                  className="mt-1 w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm"
                  value={form.category}
                  onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                />
              </label>
              <label className="text-xs text-slate-600 md:col-span-1">
                Type
                <select
                  className="mt-1 w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm"
                  value={form.direction}
                  onChange={(e) => setForm((f) => ({ ...f, direction: e.target.value as LedgerDirection }))}
                >
                  <option value="in">Income / receipt</option>
                  <option value="out">Expense / payout</option>
                </select>
              </label>
              <div className="flex gap-2 md:col-span-1">
                <label className="text-xs text-slate-600 flex-1">
                  Amount (USD)
                  <input
                    className="mt-1 w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm"
                    value={form.amount}
                    onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                    inputMode="decimal"
                    placeholder="0.00"
                  />
                </label>
                <button
                  type="button"
                  onClick={addTransaction}
                  className="self-end mb-0.5 shrink-0 rounded-md bg-slate-900 text-white text-sm px-3 py-2 hover:bg-slate-800"
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="rounded-xl border border-slate-200 overflow-hidden">
          <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex justify-between items-center">
            <h3 className="text-sm font-semibold text-slate-800">Categorized totals</h3>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-white text-slate-500 text-left">
              <tr>
                <th className="px-4 py-2 font-medium">Category</th>
                <th className="px-4 py-2 font-medium">Income</th>
                <th className="px-4 py-2 font-medium">Expenses</th>
                <th className="px-4 py-2 font-medium">Net</th>
              </tr>
            </thead>
            <tbody>
              {byCategory.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-slate-500">
                    No transactions yet.
                  </td>
                </tr>
              ) : (
                byCategory.map(([cat, v]) => (
                  <tr key={cat} className="border-t border-slate-100">
                    <td className="px-4 py-2 font-medium text-slate-800">{cat}</td>
                    <td className="px-4 py-2 text-emerald-700">{formatMoney(v.in)}</td>
                    <td className="px-4 py-2 text-rose-700">{formatMoney(v.out)}</td>
                    <td className="px-4 py-2">{formatMoney(v.in - v.out)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div id="reconciliation-print" className="print:block">
          <div className="rounded-xl border border-slate-200 overflow-hidden">
            <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex flex-wrap gap-2 justify-between items-center">
              <h3 className="text-sm font-semibold text-slate-800">Transaction audit trail</h3>
              <button
                type="button"
                onClick={handlePrintPdf}
                className="text-sm rounded-md border border-slate-300 bg-white px-3 py-1.5 hover:bg-slate-50 print:hidden"
              >
                Print / Save as PDF
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-white text-slate-500 text-left">
                  <tr>
                    <th className="px-4 py-2 font-medium">Date</th>
                    <th className="px-4 py-2 font-medium">Description</th>
                    <th className="px-4 py-2 font-medium">Category</th>
                    <th className="px-4 py-2 font-medium">Type</th>
                    <th className="px-4 py-2 font-medium text-right">Amount</th>
                    {!readOnly && <th className="px-4 py-2 w-10 print:hidden" />}
                  </tr>
                </thead>
                <tbody>
                  {[...transactions]
                    .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))
                    .map((t) => (
                      <tr key={t.id} className="border-t border-slate-100">
                        <td className="px-4 py-2 whitespace-nowrap text-slate-700">{t.date}</td>
                        <td className="px-4 py-2 text-slate-800">{t.description}</td>
                        <td className="px-4 py-2 text-slate-600">{t.category}</td>
                        <td className="px-4 py-2">{t.direction === 'in' ? 'Income' : 'Expense'}</td>
                        <td className={`px-4 py-2 text-right font-medium ${t.direction === 'in' ? 'text-emerald-700' : 'text-rose-700'}`}>
                          {t.direction === 'in' ? '+' : '−'}
                          {formatMoney(t.amountCents)}
                        </td>
                        {!readOnly && (
                          <td className="px-2 print:hidden">
                            <button
                              type="button"
                              className="text-xs text-red-600 hover:underline"
                              onClick={() => removeTransaction(t.id)}
                            >
                              Remove
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className={`rounded-xl border p-4 ${zeroOk ? 'border-emerald-300 bg-emerald-50/60' : 'border-amber-300 bg-amber-50/60'}`}>
          <h3 className="text-sm font-semibold text-slate-900 mb-2">Zero-out validation</h3>
        <ul className="text-sm text-slate-700 space-y-1 list-disc list-inside">
          <li>Net balance is exactly {formatMoney(0)} — currently {formatMoney(balanceCents)}.</li>
          <li>Executor confirms residual cash accounts are closed to zero.</li>
        </ul>
          {!readOnly && (
            <label className="mt-4 flex items-start gap-2 text-sm text-slate-800 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1"
                checked={confirmedZero}
                disabled={!zeroOk}
                onChange={(e) => {
                  setConfirmedZero(e.target.checked);
                  setDirty(true);
                }}
              />
              <span>
                I confirm the estate operating account(s) reconcile to <strong>$0.00</strong> and the ledger above is
                accurate for final reporting.
              </span>
            </label>
          )}
          {readOnly && confirmedZero && (
            <p className="mt-2 text-sm text-emerald-800">Zero balance was confirmed before archive.</p>
          )}
         </div>
        </div>
      </div>

      {isLoading && <p className="text-sm text-slate-500">Saving ledger…</p>}

      <style>{`
        @media print {
          body * { visibility: hidden; }
          #reconciliation-print, #reconciliation-print * { visibility: visible; }
          #reconciliation-print { position: absolute; left: 0; top: 0; width: 100%; }
        }
      `}</style>
    </div>
  );
}
